import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import * as StorageBrowserStore from '../../../redux/middleware/StorageBrowser.Middleware';
import { StorageBrowseViewModel, StorageFileViewModel } from '../../../models/Entities/AzureFiles/StorageBrowser';
import { ErrorType } from '../../../models/HttpError';
import { StorageEntityRef, StorageOwnerNames, getStorageFileExtension } from '../../../functions/StorageBrowser.Functions';
import { LinkResultStatus, getLinkResultStatus } from '../../../functions/StorageManagement.Functions';
import { selectStorageOwnerNames } from '../FilesExplorer/FilesExplorer.Functions';

//#region Props

interface IProps {
  open: boolean;
  onDismiss: () => void;
  /** Already translated title ("Vincular archivo existente") */
  title: string;
  /** Folder shown first (relative to the tenant; "" = root) */
  initialPath?: string;
  /** Links one file; rejects with ErrorType (409 = already linked) */
  linkFile: (path: string) => Promise<unknown>;
  /** Called once after a run that linked at least one file (refresh the owner list) */
  onLinked: (count: number) => void;
  /** Only files with these extensions are listed (lower case, no dot); undefined = any (spec 005: xlsx/xls/csv) */
  allowedExtensions?: string[];
}

interface ConnectedProps {
  ownerNames: StorageOwnerNames;
}

interface DispatchProps {
  BrowsePickerFolder: (path: string, continuationToken?: string | null) => Promise<StorageBrowseViewModel>;
  ResolveEntityNames: (refs: StorageEntityRef[]) => Promise<void>;
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState): ConnectedProps => ({
  ownerNames: selectStorageOwnerNames(state),
});

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): DispatchProps => ({
  BrowsePickerFolder: (path, continuationToken) =>
    dispatch(StorageBrowserStore.ActionCreators.BrowsePickerFolder(path, continuationToken)),
  ResolveEntityNames: (refs) => dispatch(StorageBrowserStore.ActionCreators.ResolveEntityNames(refs)),
});

//#endregion

/** Files of the folder that can be picked: every file, or only the allowed extensions (case-insensitive). */
export const filterPickerFiles = <T extends Pick<StorageFileViewModel, 'name' | 'extension'>>(
  files: T[],
  allowedExtensions: string[] | undefined
): T[] => {
  const allowed = (allowedExtensions ?? []).map((extension) => extension.trim().replace(/^\./, '').toLowerCase()).filter(Boolean);
  if (allowed.length === 0) return files;
  return files.filter((file) => allowed.includes(getStorageFileExtension(file)));
};

//#region Link results

export interface PickerLinkResult {
  path: string;
  name: string;
  status: LinkResultStatus;
  /** Error detail (status = error) */
  message?: string;
}

/** Runs the links one by one (sequential) and returns one result per file; never rejects. */
export const linkSequentially = async (
  files: Pick<StorageFileViewModel, 'path' | 'name'>[],
  linkFile: (path: string) => Promise<unknown>,
  onResult?: (result: PickerLinkResult) => void
): Promise<PickerLinkResult[]> => {
  const results: PickerLinkResult[] = [];
  for (const file of files) {
    let result: PickerLinkResult;
    try {
      await linkFile(file.path);
      result = { path: file.path, name: file.name, status: 'linked' };
    } catch (error: unknown) {
      const typed = error as Partial<ErrorType> | undefined;
      const details = (typed?.Errors ?? []).filter(Boolean);
      result = {
        path: file.path,
        name: file.name,
        status: getLinkResultStatus({ ErrorCode: typed?.ErrorCode ?? 0 }),
        message: details.length > 0 ? details.join(' ') : typed?.ErrorMessage,
      };
    }
    results.push(result);
    onResult?.(result);
  }
  return results;
};

//#endregion
